import { isNull } from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Map as GoogleMap, useMap } from "@vis.gl/react-google-maps";

// Imported actions
import { searchChargingStationsFull } from "../../../../actions/search.js";

// Imported assets
import usData from "../../../../assets/data/2023_us_state_500K.json";

// Imported components
import Marker from "./Marker.js";

// Imported contexts
import { useUserContext } from "../../../../contexts/User.tsx";

// Imported utils
import { isLoggedIn } from "../../../../utils/user.js";

const mapStyle = {
  width: "100%",
  height: "100%",
};

const mapOptions = {
  clickableIcons: false,
  // colorScheme: "DARK",
  fullscreenControl: false,
  mapTypeControl: false,
  maxZoom: 22,
  minZoom: 0,
  streetViewControl: false,
  styles: [
    {
      featureType: "administrative.locality",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "poi",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "landscape",
      elementType: "labels",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "transit",
      stylers: [{ visibility: "off" }],
    },
    {
      featureType: "water",
      elementType: "geometry",
      stylers: [{ color: "#08609B" }],
    },
  ],
  zoomControl: false,
};

const Map = () => {
  const map = useMap();
  const appLocation = useLocation();
  const navigate = useNavigate();

  const { user } = useUserContext();

  const [mapCenter, setMapCenter] = useState(
    // Center of mainland US
    {
      // TODO: remove temp checks on userId
      lat: user.userId === "tony.k.walters@cnmp.com" ? 37.1661 : 39.5,
      lng: user.userId === "tony.k.walters@cnmp.com" ? -119.4494 : -98.35,
    }

    // Center of California
    // {
    //   lat: 37.1661,
    //   lng: -119.4494,
    // }
  );

  const [mapZoom, setMapZoom] = useState(
    // TODO: remove temp checks on userId
    user.userId === "tony.k.walters@cnmp.com" ? 6.6 : 5 // For mainland US
    // 6.6 // For California
  );

  const [markers, setMarkers] = useState([]);

  const [windowBounds, setWindowBounds] = useState([]);

  useEffect(() => {
    // Outer bounds to cover the world
    const outer = [
      new window.google.maps.LatLng(85, 180),
      new window.google.maps.LatLng(85, 90),
      new window.google.maps.LatLng(85, 0),
      new window.google.maps.LatLng(85, -90),
      new window.google.maps.LatLng(85, -180),
      new window.google.maps.LatLng(0, -180),
      new window.google.maps.LatLng(-85, -180),
      new window.google.maps.LatLng(-85, -90),
      new window.google.maps.LatLng(-85, 0),
      new window.google.maps.LatLng(-85, 90),
      new window.google.maps.LatLng(-85, 180),
      new window.google.maps.LatLng(0, 180),
      new window.google.maps.LatLng(85, 180),
    ];

    // Inner bounds to cut out designated area from outer bounds
    let innerBoundaries = [];
    usData.features.forEach((feature) => {
      // TODO: remove temp checks on userId
      if (user.userId === "tony.k.walters@cnmp.com") {
        if (feature.attributes.NAME === "California") {
          let arr = feature.geometry.rings;
          arr.forEach((coords) => {
            let newBoundary = coords.map((latLng) => {
              return new window.google.maps.LatLng(latLng[1], latLng[0]);
            });
            innerBoundaries.push(newBoundary);
          });
        }
      } else {
        // if (feature.attributes.NAME === "California") {
        if (
          !(
            feature.attributes.NAME === "Hawaii" ||
            feature.attributes.NAME === "Alaska" ||
            feature.attributes.NAME === "Puerto Rico" ||
            feature.attributes.NAME === "American Samoa" ||
            feature.attributes.NAME === "Guam" ||
            feature.attributes.NAME === "United States Virgin Islands" ||
            feature.attributes.NAME ===
              "Commonwealth of the Northern Mariana Islands"
          )
        ) {
          let arr = feature.geometry.rings;
          arr.forEach((coords) => {
            let newBoundary = coords.map((latLng) => {
              return new window.google.maps.LatLng(latLng[1], latLng[0]);
            });
            innerBoundaries.push(newBoundary);
          });
        }
      }
    });

    const innerPolygon = new window.google.maps.Polygon({
      paths: [...innerBoundaries],
      // map: map,
      // strokeColor: "#08609B",
      // strokeOpacity: 0,
      // strokeWeight: 0,
      // fillColor: "#08609B",
      // fillOpacity: 0,
    });

    const polygon = new window.google.maps.Polygon({
      paths: [outer, ...innerBoundaries],
      map: map,
      strokeColor: "#08609B",
      strokeOpacity: 1,
      strokeWeight: 1,
      fillColor: "#08609B",
      fillOpacity: 1,
    });

    // Get bounds for map based on inner polygon object
    let bounds = new window.google.maps.LatLngBounds();
    let paths = innerPolygon.getPaths();
    let path;
    for (let i = 0; i < paths.getLength(); i++) {
      path = paths.getAt(i);
      for (let j = 0; j < path.getLength(); j++) {
        bounds.extend(path.getAt(j));
      }
    }

    map?.fitBounds(bounds, 110);
  }, [map, windowBounds]);

  useEffect(() => {
    const handleResize = () => {
      setWindowBounds([window.innerWidth, window.innerHeight]);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // useEffect(() => {
  // const place = new window.google.maps.places.Place({
  //   id: "ChIJt1YYm3QUQIcR_6eQSTGDVMc",
  // });

  // // const getFields = async (place) => {
  // //   await place
  // //     .fetchFields({
  // //       fields: ["displayName", "formattedAddress", "viewport", "location"],
  // //     })
  // //     .then((place) => {
  // //       return place;
  // //     });
  // // };

  // // getFields(place).then(() => {
  // //   console.log(place);
  // // });

  // // Outer bounds to cover the world
  // const outer = [
  //   new window.google.maps.LatLng(85, 180),
  //   new window.google.maps.LatLng(85, 90),
  //   new window.google.maps.LatLng(85, 0),
  //   new window.google.maps.LatLng(85, -90),
  //   new window.google.maps.LatLng(85, -180),
  //   new window.google.maps.LatLng(0, -180),
  //   new window.google.maps.LatLng(-85, -180),
  //   new window.google.maps.LatLng(-85, -90),
  //   new window.google.maps.LatLng(-85, 0),
  //   new window.google.maps.LatLng(-85, 90),
  //   new window.google.maps.LatLng(-85, 180),
  //   new window.google.maps.LatLng(0, 180),
  //   new window.google.maps.LatLng(85, 180),
  // ];

  // // Test an inner bounds
  // const inner = coloradoCoords.map((coords) => {
  //   return new window.google.maps.LatLng(coords[1], coords[0]);
  // });

  // const polygon = new window.google.maps.Polygon({
  //   paths: [outer, inner],
  //   map: map,
  //   strokeColor: "#71d3e7",
  //   strokeOpacity: 1,
  //   // strokeWeight: 1,
  //   fillColor: "#71d3e7",
  //   fillOpacity: 1,
  // });
  // }, [map]);

  // Grabs station data from the API on load and on interval
  useEffect(() => {
    const searchStationsFull = async () => {
      await searchChargingStationsFull(false, 0, 99999)
        .then(async (stations) => {
          setMarkers(stations);
        })
        .catch((err) => {
          // handle error
        });
    };

    const attemptFetch = () => {
      if (!isLoggedIn()) {
        navigate("/login", {
          replace: true,
          state: { path: appLocation.pathname },
        });
      } else {
        searchStationsFull();
      }
    };

    attemptFetch();

    const interval = setInterval(attemptFetch, 30000);

    return () => clearInterval(interval);
  }, []);

  // Force a resize after load to reach proper zoom & center
  useEffect(() => {
    const handleInitialResize = () => {
      setWindowBounds([window.innerWidth, window.innerHeight]);
    };

    const timeout = setTimeout(handleInitialResize, 1000);

    return () => clearTimeout(timeout);
  }, [setWindowBounds]);

  // useEffect(() => {
  //   if (map && markers) {
  //     setMapCenter(null);
  //     setMapZoom(null);
  //     let bounds = new window.google.maps.LatLngBounds();
  //     Object.values(markers)
  //       .map((marker) => ({ lat: Number(marker.lat), lng: Number(marker.lon) }))
  //       .forEach((marker) => bounds.extend(marker));
  //     console.log(bounds);
  //     map.fitBounds(bounds, 20);
  //   }
  // }, [map, markers]);

  // const handleCameraChange = useCallback((e) => {
  //   setMapCenter(e.detail.center);
  //   setMapZoom(e.detail.zoom);
  // });

  return (
    <>
      {/* TODO: Remove temp userId check in mapTypeId below */}
      <GoogleMap
        {...mapOptions}
        center={!isNull(mapCenter) ? mapCenter : null}
        className={"map-view-map"}
        disableDefaultUI={true}
        gestureHandling={"none"}
        mapId={"b01392efe0cdf38a"}
        mapTypeId={
          user.userId === "tony.k.walters@cnmp.com" ? "roadmap" : "hybrid"
        }
        // onCameraChanged={handleCameraChange}
        reuseMaps={false}
        style={mapStyle}
        zoom={!isNull(mapZoom) ? mapZoom : null}
      >
        {markers &&
          Object.values(markers)
            .sort((a, b) => {
              // Sort by status to ensure proper zIndex
              const getStationVal = (station) => {
                if (station.locationStatusType.id === 3) {
                  // Cyber Attack
                  return 4;
                } else if (station.locationStatusType.id === 2) {
                  // Faulted
                  return 3;
                } else if (station.maintenanceActive) {
                  // Under Maintenance
                  return 2;
                } else {
                  // Available, and default or other
                  return 1;
                }
              };

              return getStationVal(a) - getStationVal(b);
            })
            .map((marker, i) => {
              return (
                <Marker
                  data={marker}
                  index={i}
                  key={`marker-${marker.id}`}
                  position={{
                    lat: Number(marker.lat),
                    lng: Number(marker.lon),
                  }}
                />
              );
            })}
      </GoogleMap>
    </>
  );
};

export default Map;
